import { type FC, useState, useMemo } from 'react';
import FilterBadge, { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

const FrequencyChart: FC = () => {
  const { frequencyData, statsSummary, loading, advancedFilters, resetFilters } = useStatisticsData();
  const [sortBy, setSortBy] = useState<'number' | 'frequency'>('frequency');

  const sortedData = useMemo(() => {
    if (!frequencyData) return [];
    const entries = Array.from(frequencyData.entries());
    if (sortBy === 'frequency') {
      return entries.sort((a, b) => b[1] - a[1]);
    }
    return entries.sort((a, b) => a[0] - b[0]);
  }, [frequencyData, sortBy]);

  const maxFreq = sortedData[0]?.[1] || 1;

  const filterKey = useMemo(() => {
    return `${advancedFilters.numberRanges.join(',')}|${advancedFilters.dayOfWeeks.join(',')}|${advancedFilters.prizeRange}|${sortBy}`;
  }, [advancedFilters, sortBy]);

  const getBarColor = (freq: number) => {
    const ratio = freq / maxFreq;
    if (ratio >= 0.8) return 'bg-secondary-500';
    if (ratio >= 0.6) return 'bg-secondary-400';
    if (ratio >= 0.4) return 'bg-secondary-300';
    if (ratio >= 0.2) return 'bg-secondary-200';
    return 'bg-background-300';
  };

  const getTextColor = (freq: number) => {
    const ratio = freq / maxFreq;
    if (ratio >= 0.8) return 'text-secondary-800';
    if (ratio >= 0.6) return 'text-secondary-700';
    return 'text-foreground-600';
  };

  if (loading) {
    return (
      <div>
        <div className="h-6 bg-background-200 rounded w-32 mb-5 animate-pulse"></div>
        <div className="space-y-1.5">
          {Array.from({ length: 15 }).map((_, i) => (
            <div key={i} className="h-7 bg-background-200 rounded-md animate-pulse" style={{ width: `${30 + Math.random() * 70}%` }}></div>
          ))}
        </div>
      </div>
    );
  }

  if (!statsSummary) {
    return (
      <div>
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <h2 className="font-heading text-xl text-foreground-900">번호별 출현 빈도</h2>
            <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
          </div>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => setSortBy('frequency')}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer bg-background-50 text-foreground-900 shadow-sm"
            >
              빈도순
            </button>
            <button
              onClick={() => setSortBy('number')}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer text-foreground-400 hover:text-foreground-600"
            >
              번호순
            </button>
          </div>
        </div>
        <EmptyFilterState />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <h2 className="font-heading text-xl text-foreground-900">번호별 출현 빈도</h2>
          <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
        </div>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
          <button
            onClick={() => setSortBy('frequency')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${sortBy === 'frequency' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
          >
            빈도순
          </button>
          <button
            onClick={() => setSortBy('number')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${sortBy === 'number' ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}
          >
            번호순
          </button>
        </div>
      </div>

      <div key={filterKey} className="space-y-1.5 max-h-[520px] overflow-y-auto pr-2 animate-[contentFadeIn_0.35s_ease-out]">
        {sortedData.map(([num, freq]) => {
          const percentage = Math.round((freq / maxFreq) * 100);
          const isHottest = statsSummary && freq === statsSummary.hottestCount && num === statsSummary.hottestNumber;
          const isColdest = statsSummary && freq === statsSummary.coldestCount && num === statsSummary.coldestNumber;
          return (
            <div key={num} className="flex items-center gap-3 group">
              <span className={`w-8 text-right text-xs font-bold flex-shrink-0 ${isHottest ? 'text-secondary-700' : 'text-foreground-600'}`}>
                {num}
              </span>
              <div className="flex-1 h-7 bg-background-100 rounded-md overflow-hidden relative">
                <div
                  className={`h-full rounded-md transition-all duration-500 ${getBarColor(freq)}`}
                  style={{ width: `${percentage}%` }}
                />
                <span className={`absolute inset-y-0 left-0 flex items-center pl-2 text-xs font-bold ${getTextColor(freq)}`}>
                  {freq}회
                </span>
              </div>
              <span className="w-10 text-xs text-foreground-300 text-right flex-shrink-0">
                {isHottest ? (
                  <span className="text-secondary-700 font-bold">HOT</span>
                ) : isColdest ? (
                  <span className="text-secondary-600 font-bold">COLD</span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-5 mt-5 pt-4 border-t border-background-200/60">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-secondary-500" />
          <span className="text-xs text-foreground-500">상위 20%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-secondary-400" />
          <span className="text-xs text-foreground-500">상위 40%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-secondary-300" />
          <span className="text-xs text-foreground-500">중간</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-secondary-200" />
          <span className="text-xs text-foreground-500">하위</span>
        </div>
      </div>
    </div>
  );
};

export default FrequencyChart;